import { Box, Collapse, Group, Pagination, Table } from "@mantine/core";
import {
  IconArrowDown,
  IconArrowsSort,
  IconArrowUp,
} from "@tabler/icons-react";
import { flexRender, type Table as ITable, type Row } from "@tanstack/react-table";
import React from "react";

export interface ITabla<T> {
  tabla: ITable<T>;
  renderAcordion?: (row: Row<T>) => React.ReactNode;
  maxHeight?: number | string;
  minWidth?: number | string;
  // 🆕 Nuevas props para edición en línea
  modoEdicion?: boolean;
  filaEnEdicion?: string | null;
  onCambioValor?: (rowId: string, columnId: string, valor: any) => void;
}

const Tabla = <T,>({
  tabla,
  renderAcordion,
  minWidth,
  modoEdicion = false,
  filaEnEdicion = null
}: ITabla<T>) => {
  const columnasFijas = ["actions"];

  return (
    <div
      style={{
        background: "#fff",
        padding: "5px",
        borderRadius: "8px",
        overflow: "hidden",
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "auto",
          position: "relative",
        }}
      >
        <Table
          highlightOnHover
          stickyHeader
          style={{
            width: "100%",
            minWidth: minWidth ?? 900,
            tableLayout: "auto",
            borderCollapse: "collapse",
            borderSpacing: 0,
            background: "#fff",
            fontSize: 12,
            borderRadius: 8,
          }}
        >
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: "#fff",
            }}
          >
            {tabla.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const esColumnaFija = columnasFijas.includes(header.column.id);
                  return (
                    <th
                      key={header.id}
                      onClick={
                        header.column.getCanSort()
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                      style={{
                        padding: "8px 8px",
                        background: esColumnaFija ? "#fff" : "transparent",
                        fontWeight: 600,
                        borderBottom: "1px solid #eaeaea",
                        textAlign: "center",
                        width: esColumnaFija ? 110 : "auto",
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                        whiteSpace: "nowrap",
                        position: esColumnaFija ? "sticky" : undefined,
                        left: esColumnaFija ? 0 : undefined,
                        zIndex: esColumnaFija ? 30 : 2,
                        boxShadow: esColumnaFija
                          ? "2px 0 4px rgba(0,0,0,0.05)"
                          : undefined,
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {header.column.getCanSort() && (
                        <span
                          style={{
                            marginLeft: 6,
                            display: "inline-flex",
                            verticalAlign: "middle",
                          }}
                        >
                          {{
                            asc: (
                              <IconArrowUp
                                color="#6f736b"
                                size={14}
                                stroke={2}
                              />
                            ),
                            desc: (
                              <IconArrowDown
                                color="#6f736b"
                                size={14}
                                stroke={2}
                              />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                              <IconArrowsSort
                                color="#6f736b"
                                size={14}
                                stroke={2}
                              />
                            )}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {tabla.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={tabla.getVisibleLeafColumns().length}
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#888",
                    fontStyle: "italic",
                  }}
                >
                  No hay registros disponibles
                </td>
              </tr>
            ) : (
              tabla.getRowModel().rows.map((row) => {
                // 🆕 Determinar si esta fila está en edición
                const estaFilaEnEdicion = modoEdicion && filaEnEdicion === row.id;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      style={{
                        cursor: "pointer",
                        transition: "background 0.2s",
                        borderBottom: "1px solid #f0f0f0",
                        background: estaFilaEnEdicion ? "#0074d90e" : "transparent",
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const esColumnaFija = columnasFijas.includes(cell.column.id);

                        return (
                          <td
                            key={cell.id}
                            style={{
                              padding: "8px 8px",
                              background: esColumnaFija ? "#fff" : "transparent",
                              color: "#222",
                              verticalAlign: "middle",
                              textAlign: "center",
                              width: esColumnaFija ? 110 : "auto",
                              whiteSpace: "nowrap",
                              position: esColumnaFija ? "sticky" : undefined,
                              left: esColumnaFija ? 0 : undefined,
                              zIndex: esColumnaFija ? 3 : undefined,
                              boxShadow: esColumnaFija
                                ? "2px 0 4px rgba(0,0,0,0.05)"
                                : undefined,
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td
                          colSpan={tabla.getVisibleLeafColumns().length}
                          style={{
                            padding: 0,
                            background: "#f6faff",
                            borderBottom: "1px solid #eaeaea",
                          }}
                        >
                          <Collapse in={row.getIsExpanded()}>
                            <Box p="md">
                              {renderAcordion ? renderAcordion(row) : null}
                            </Box>
                          </Collapse>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <Group justify="end" mt="sm" mb="sm">
        <Pagination
          value={tabla.getState().pagination.pageIndex + 1}
          onChange={(page) => tabla.setPageIndex(page - 1)}
          total={tabla.getPageCount()}
          siblings={1}
          withEdges
          radius="lg"
          size="sm"
        />
      </Group>
    </div>
  );
};

export default Tabla;